import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/pt';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';
import { Loader2Icon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { DynamicFieldTypes } from '../../entities/dynamic-field-type';

DynamicFieldSelectorDialog.propTypes = {
  title: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onCreated: PropTypes.func,
  value: PropTypes.object,
  titleOnly: PropTypes.bool,
};

/*
 * Selector for a single dynamic field (title + data-type + optional value).
 * Used by the resource/flow forms when the user clicks "Add field". Pure
 * shadcn now — title input, data-type Select, type-specific value editor
 * (Input / Checkbox / DatePicker).
 */
export default function DynamicFieldSelectorDialog({
  title,
  open,
  onClose,
  onCreated,
  value,
  titleOnly = false,
}) {
  const { t } = useTranslation();

  const [error, setError] = useState(null);
  const [loading] = useState(false);
  const [fieldTitle, setFieldTitle] = useState('');
  const [dataType, setDataType] = useState('');
  const [fieldValue, setFieldValue] = useState('');

  const resetState = () => {
    setError(null);
    setFieldTitle('');
    setDataType('');
    setFieldValue('');
  };

  const onOpenChange = (next) => {
    if (!next) {
      if (loading) return;
      resetState();
      onClose?.();
    }
  };

  const onConfirm = () => {
    if (fieldTitle.length === 0) {
      setError('title_required');
      return;
    }
    if (dataType === '' || dataType === 'none') {
      setError('dataType_required');
      return;
    }
    if (!titleOnly && (fieldValue === '' || fieldValue === undefined || fieldValue === null)) {
      setError('fieldValue_required');
      return;
    }
    const data = { title: fieldTitle, dataType };
    if (!titleOnly) data.value = fieldValue;
    onCreated(data);
    resetState();
  };

  const locale = (navigator.language || navigator.userLanguage).substring(0, 2);
  const dateFormat = locale === 'en' ? 'MM/DD/YYYY' : 'DD/MM/YYYY';

  useEffect(() => {
    if (value) {
      setFieldTitle(value.title);
      setDataType(value.dataType);
      if (!titleOnly) setFieldValue(value.value);
    }
  }, [titleOnly, value]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onInteractOutside={(e) => { if (loading) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (loading) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{title ?? t('components.dynamic-field-selector.title')}</DialogTitle>
          <DialogDescription className="sr-only">
            {title ?? t('components.dynamic-field-selector.title')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label
              htmlFor="dfs-title"
              className={cn(
                'text-sm font-medium',
                "after:ml-0.5 after:text-destructive after:content-['*']",
              )}
            >
              {t('components.dynamic-field-selector.field-title')}
            </Label>
            <Input
              id="dfs-title"
              value={fieldTitle}
              onChange={(e) => setFieldTitle(e.target.value)}
              required
              aria-invalid={error === 'title_required'}
            />
          </div>

          <div className="space-y-1.5">
            <Label
              htmlFor="dfs-type"
              className="text-sm font-medium after:ml-0.5 after:text-destructive after:content-['*']"
            >
              {t('components.dynamic-field-selector.field-data-type.title')}
            </Label>
            <Select value={dataType} onValueChange={setDataType}>
              <SelectTrigger id="dfs-type" aria-invalid={error === 'dataType_required'}>
                <SelectValue placeholder={t('components.dynamic-field-selector.field-data-type.title')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={DynamicFieldTypes.TEXT}>
                  {t('components.dynamic-field-selector.field-data-type.text')}
                </SelectItem>
                <SelectItem value={DynamicFieldTypes.NUMBER}>
                  {t('components.dynamic-field-selector.field-data-type.number')}
                </SelectItem>
                <SelectItem value={DynamicFieldTypes.DATE}>
                  {t('components.dynamic-field-selector.field-data-type.date')}
                </SelectItem>
                <SelectItem value={DynamicFieldTypes.BOOLEAN}>
                  {t('components.dynamic-field-selector.field-data-type.boolean')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {!titleOnly ? (
            <div className="space-y-1.5">
              {dataType === DynamicFieldTypes.BOOLEAN ? (
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={Boolean(fieldValue)}
                    onCheckedChange={(v) => setFieldValue(Boolean(v))}
                  />
                  <span>{t('components.dynamic-field-selector.field-boolean-label')}</span>
                </label>
              ) : dataType === DynamicFieldTypes.DATE ? (
                <DatePicker
                  label={t('components.dynamic-field-selector.field-date-label')}
                  value={fieldValue ? dayjs(fieldValue) : null}
                  onChange={(val) => setFieldValue(val ? val.toISOString() : '')}
                  format={dateFormat}
                  locale={locale}
                />
              ) : (
                <>
                  <Label
                    htmlFor="dfs-value"
                    className="text-sm font-medium after:ml-0.5 after:text-destructive after:content-['*']"
                  >
                    {t('components.dynamic-field-selector.field-value')}
                  </Label>
                  <Input
                    id="dfs-value"
                    type={dataType === DynamicFieldTypes.NUMBER ? 'number' : 'text'}
                    value={fieldValue ?? ''}
                    onChange={(e) => setFieldValue(e.target.value)}
                    required
                    aria-invalid={error === 'fieldValue_required'}
                  />
                </>
              )}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={onConfirm} disabled={loading}>
            {loading ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
